/**
 * ImpersonationBanner — sticky strip shown when an admin is currently
 * "Viewing as" another user (CO-113). Clicking Exit restores the
 * admin's real identity and reloads the auth state.
 *
 * The banner sits ABOVE the Header so admins can never miss that
 * they're on someone else's view.
 */

import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../services/api';

export function ImpersonationBanner() {
  const { user, refetch } = useAuth();
  const [exiting, setExiting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reflect any cross-tab impersonation changes on focus.
  useEffect(() => {
    function onFocus() {
      void refetch();
    }
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [refetch]);

  if (!user?.impersonating) return null;

  async function exit() {
    setExiting(true);
    setError(null);
    try {
      await api.stopImpersonation();
      await refetch();
      // Reload so any cached page state (file lists, dashboards)
      // is refetched with the admin identity.
      window.location.reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to exit impersonation');
      setExiting(false);
    }
  }

  return (
    <div
      role="alert"
      style={{
        background: 'linear-gradient(90deg, #fb923c, #f97316)',
        color: '#1f2937',
        padding: '8px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        fontSize: 13,
        fontWeight: 500,
        borderBottom: '1px solid rgba(0,0,0,0.15)',
      }}
    >
      <span style={{ fontSize: 16 }} aria-hidden>
        👁
      </span>
      <span>
        Viewing as <strong>{user.email}</strong>
        <span style={{ opacity: 0.7, marginLeft: 6 }}>
          (role: {user.isAdmin ? 'admin' : 'user'})
        </span>
        <span style={{ opacity: 0.7, marginLeft: 8 }}>
          — your real account is {user.impersonating.realEmail}
        </span>
      </span>
      <span style={{ flex: 1 }} />
      {error && <span style={{ color: '#7f1d1d' }}>{error}</span>}
      {/* eslint-disable-next-line no-restricted-syntax -- the Exit view-as control is a black pill tuned to sit on the fixed orange impersonation-banner gradient; a one-off banner affordance, not a components/ui Button. Deferred per Track E. */}
      <button
        type="button"
        onClick={() => void exit()}
        disabled={exiting}
        style={{
          background: 'rgba(0,0,0,0.85)',
          color: '#fff',
          border: 'none',
          padding: '5px 14px',
          borderRadius: 6,
          fontWeight: 600,
          fontSize: 12,
          cursor: exiting ? 'wait' : 'pointer',
          opacity: exiting ? 0.6 : 1,
        }}
      >
        {exiting ? 'Exiting…' : 'Exit view-as'}
      </button>
    </div>
  );
}
